import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid } from '@mui/material';
import { Color } from '../../../assets/color';
import { useNavigate } from 'react-router-dom';
import RouteHeader from '../../../components/common/RouteHeader';
import * as SuperadminActions from '../../../redux/actions/superadminAction';
import { AlertTriangle, Clock, CheckCircle, XCircle, Bug, TrendingUp, BarChart2, Activity } from 'lucide-react';

const BugDashboard = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { bugStatsData } = useSelector(state => state?.superadminReducer);

    useEffect(() => {
        dispatch(SuperadminActions.getBugStats());
    }, []);

    const stats = bugStatsData || {};

    const statCards = [
        { label: 'Total Issues', value: stats?.total || 0, color: '#5045E5', icon: <Bug size={24} />, bg: '#EDE9FF' },
        { label: 'Open', value: stats?.open || 0, color: '#F59E0B', icon: <AlertTriangle size={24} />, bg: '#FEF3C7' },
        { label: 'In Progress', value: stats?.inProgress || 0, color: '#3B82F6', icon: <Activity size={24} />, bg: '#DBEAFE' },
        { label: 'In Review', value: stats?.inReview || 0, color: '#8B5CF6', icon: <Clock size={24} />, bg: '#EDE9FE' },
        { label: 'Testing', value: stats?.testing || 0, color: '#06B6D4', icon: <TrendingUp size={24} />, bg: '#CFFAFE' },
        { label: 'Closed', value: stats?.closed || 0, color: '#10B981', icon: <CheckCircle size={24} />, bg: '#D1FAE5' },
        { label: 'Rejected', value: stats?.rejected || 0, color: '#EF4444', icon: <XCircle size={24} />, bg: '#FEE2E2' },
        { label: 'Avg Resolution (hrs)', value: stats?.avgResolutionTimeHours || 0, color: '#6366F1', icon: <BarChart2 size={24} />, bg: '#E0E7FF' },
    ];

    const priorityColors = { Critical: '#EF4444', High: '#F59E0B', Medium: '#3B82F6', Low: '#10B981' };

    const getTimeAgo = (dateStr) => {
        const now = new Date();
        const date = new Date(dateStr);
        const diffMs = now - date;
        const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffHrs / 24);
        if (diffDays > 0) return `${diffDays}d ago`;
        if (diffHrs > 0) return `${diffHrs}h ago`;
        return 'Just now';
    };

    const getOpenDuration = (dateStr) => {
        const now = new Date();
        const date = new Date(dateStr);
        const diffMs = now - date;
        const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffHrs / 24);
        if (diffDays > 0) return `${diffDays} days`;
        return `${diffHrs} hours`;
    };

    return (
        <>
            <RouteHeader title="Bug Tracker Dashboard" sub_title="Overview of all issues and their current status" />

            {/* Stats Cards */}
            <Grid container spacing={3} style={{ marginBottom: '30px' }}>
                {statCards.map((card, idx) => (
                    <Grid item lg={3} md={4} sm={6} xs={12} key={idx}>
                        <div style={{
                            backgroundColor: Color.white, borderRadius: '12px', padding: '20px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)', cursor: 'pointer',
                            border: `1px solid ${card.bg}`, transition: 'transform 0.2s',
                        }}
                            onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                            onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div>
                                    <p style={{ fontSize: '13px', color: '#72727B', marginBottom: '5px' }}>{card.label}</p>
                                    <h2 style={{ fontSize: '28px', fontWeight: '700', color: card.color }}>{card.value}</h2>
                                </div>
                                <div style={{ backgroundColor: card.bg, padding: '12px', borderRadius: '12px', color: card.color }}>
                                    {card.icon}
                                </div>
                            </div>
                        </div>
                    </Grid>
                ))}
            </Grid>

            {/* Priority Distribution & Module Distribution */}
            <Grid container spacing={3} style={{ marginBottom: '30px' }}>
                {/* Priority Distribution */}
                <Grid item lg={6} md={6} sm={12} xs={12}>
                    <div style={{ backgroundColor: Color.white, borderRadius: '12px', padding: '25px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px', background: "linear-gradient(to right, #6653E8, #9544EB)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                            Priority Distribution (Active)
                        </h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {['Critical', 'High', 'Medium', 'Low'].map(p => {
                                const count = stats?.priority?.[p.toLowerCase()] || 0;
                                const total = (stats?.priority?.critical || 0) + (stats?.priority?.high || 0) + (stats?.priority?.medium || 0) + (stats?.priority?.low || 0);
                                const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                                return (
                                    <div key={p}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                            <span style={{ fontSize: '14px', fontWeight: '500' }}>{p}</span>
                                            <span style={{ fontSize: '14px', color: '#72727B' }}>{count} ({pct}%)</span>
                                        </div>
                                        <div style={{ height: '8px', backgroundColor: '#F1F5F9', borderRadius: '4px', overflow: 'hidden' }}>
                                            <div style={{ height: '100%', width: `${pct}%`, backgroundColor: priorityColors[p], borderRadius: '4px', transition: 'width 0.5s' }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </Grid>

                {/* Module Distribution */}
                <Grid item lg={6} md={6} sm={12} xs={12}>
                    <div style={{ backgroundColor: Color.white, borderRadius: '12px', padding: '25px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px', background: "linear-gradient(to right, #6653E8, #9544EB)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                            Module-wise Active Issues
                        </h3>
                        {stats?.moduleDistribution?.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {stats.moduleDistribution.map((mod, idx) => (
                                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', backgroundColor: '#F8FAFC', borderRadius: '8px' }}>
                                        <span style={{ fontSize: '14px', fontWeight: '500' }}>{mod._id}</span>
                                        <span style={{
                                            fontSize: '13px', fontWeight: '600', padding: '3px 12px',
                                            borderRadius: '20px', backgroundColor: '#EDE9FF', color: '#5045E5'
                                        }}>{mod.count}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p style={{ color: '#72727B', textAlign: 'center', padding: '20px' }}>No active issues 🎉</p>
                        )}
                    </div>
                </Grid>
            </Grid>

            {/* Oldest Open & Recently Closed */}
            <Grid container spacing={3}>
                {/* Oldest Open Bugs */}
                <Grid item lg={6} md={6} sm={12} xs={12}>
                    <div style={{ backgroundColor: Color.white, borderRadius: '12px', padding: '25px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px', background: "linear-gradient(to right, #6653E8, #9544EB)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                            ⏰ Oldest Open Issues
                        </h3>
                        {stats?.oldestOpenBugs?.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {stats.oldestOpenBugs.map((bug, idx) => (
                                    <div key={idx}
                                        onClick={() => navigate(`/bug-tracker/issues/${bug._id}`)}
                                        style={{
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            padding: '12px 14px', backgroundColor: '#FFF7ED', borderRadius: '8px',
                                            cursor: 'pointer', border: '1px solid #FED7AA', transition: 'background 0.2s'
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#FFEDD5'}
                                        onMouseLeave={e => e.currentTarget.style.backgroundColor = '#FFF7ED'}
                                    >
                                        <div>
                                            <span style={{ fontSize: '12px', color: '#5045E5', fontWeight: '600' }}>{bug.bug_id}</span>
                                            <p style={{ fontSize: '14px', fontWeight: '500', marginTop: '2px' }}>{bug.title}</p>
                                            <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                                                <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px', backgroundColor: priorityColors[bug.priority] + '20', color: priorityColors[bug.priority], fontWeight: '600' }}>
                                                    {bug.priority}
                                                </span>
                                                <span style={{ fontSize: '11px', color: '#72727B' }}>{bug.module}</span>
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <p style={{ fontSize: '12px', color: '#EA580C', fontWeight: '600' }}>Open {getOpenDuration(bug.createdAt)}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p style={{ color: '#72727B', textAlign: 'center', padding: '20px' }}>No open issues 🎉</p>
                        )}
                    </div>
                </Grid>

                {/* Recently Closed */}
                <Grid item lg={6} md={6} sm={12} xs={12}>
                    <div style={{ backgroundColor: Color.white, borderRadius: '12px', padding: '25px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px', background: "linear-gradient(to right, #6653E8, #9544EB)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                            ✅ Recently Closed Issues
                        </h3>
                        {stats?.recentlyClosed?.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {stats.recentlyClosed.map((bug, idx) => {
                                    const resTime = bug.closed_at ? Math.round((new Date(bug.closed_at) - new Date(bug.createdAt)) / (1000 * 60 * 60)) : 0;
                                    return (
                                        <div key={idx} style={{
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            padding: '12px 14px', backgroundColor: '#F0FDF4', borderRadius: '8px',
                                            border: '1px solid #BBF7D0'
                                        }}>
                                            <div>
                                                <span style={{ fontSize: '12px', color: '#5045E5', fontWeight: '600' }}>{bug.bug_id}</span>
                                                <p style={{ fontSize: '14px', fontWeight: '500', marginTop: '2px' }}>{bug.title}</p>
                                                <span style={{ fontSize: '11px', color: '#72727B' }}>{bug.module}</span>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <p style={{ fontSize: '12px', color: '#16A34A', fontWeight: '600' }}>
                                                    Resolved in {resTime > 24 ? `${Math.floor(resTime / 24)}d` : `${resTime}h`}
                                                </p>
                                                <p style={{ fontSize: '11px', color: '#72727B' }}>{getTimeAgo(bug.closed_at)}</p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p style={{ color: '#72727B', textAlign: 'center', padding: '20px' }}>No closed issues yet</p>
                        )}
                    </div>
                </Grid>
            </Grid>

            {/* Quick Stats Summary */}
            <div style={{ marginTop: '30px', backgroundColor: Color.white, borderRadius: '12px', padding: '25px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '15px', background: "linear-gradient(to right, #6653E8, #9544EB)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                    📊 Quick Summary
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                    <div style={{ padding: '15px 25px', backgroundColor: '#F8FAFC', borderRadius: '10px', flex: 1, minWidth: '200px' }}>
                        <p style={{ fontSize: '13px', color: '#72727B' }}>Issues This Week</p>
                        <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#5045E5' }}>{stats?.recentBugsCount || 0}</h3>
                    </div>
                    <div style={{ padding: '15px 25px', backgroundColor: '#F8FAFC', borderRadius: '10px', flex: 1, minWidth: '200px' }}>
                        <p style={{ fontSize: '13px', color: '#72727B' }}>Valid Issues</p>
                        <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#8B5CF6' }}>{stats?.valid || 0}</h3>
                    </div>
                    <div style={{ padding: '15px 25px', backgroundColor: '#F8FAFC', borderRadius: '10px', flex: 1, minWidth: '200px' }}>
                        <p style={{ fontSize: '13px', color: '#72727B' }}>Avg Resolution Time</p>
                        <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#10B981' }}>
                            {stats?.avgResolutionTimeHours > 24
                                ? `${Math.floor(stats.avgResolutionTimeHours / 24)}d ${stats.avgResolutionTimeHours % 24}h`
                                : `${stats?.avgResolutionTimeHours || 0}h`
                            }
                        </h3>
                    </div>
                    <div style={{ padding: '15px 25px', backgroundColor: '#F8FAFC', borderRadius: '10px', flex: 1, minWidth: '200px' }}>
                        <p style={{ fontSize: '13px', color: '#72727B' }}>Resolution Rate</p>
                        <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#06B6D4' }}>
                            {stats?.total > 0 ? Math.round(((stats?.closed || 0) / stats.total) * 100) : 0}%
                        </h3>
                    </div>
                </div>
            </div>
        </>
    );
};

export default BugDashboard;
